import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import type { LinkProps } from 'next/link';
import Link from 'next/link';
import type { ComponentProps, ForwardedRef, PropsWithChildren } from 'react';
import { forwardRef } from 'react';

import type { ButtonVariantProps } from './Button';
import { buttonStyles } from './Button';
import Icon from './Icon';

type Props = ComponentProps<'a'> & {
  prefixIcon?: IconDefinition | string;
  suffixIcon?: IconDefinition | string;
  suffixIconClassName?: string;
  prefixIconClassName?: string;
  href: string;
  isInternalLink?: boolean;
  isAnimated?: boolean;
  wrapperClassName?: string;
} & ButtonVariantProps;

const NavigationButtonComponent = (
  {
    children,
    prefixIcon,
    suffixIcon,
    variant,
    size,
    prefixIconClassName,
    suffixIconClassName,
    isInternalLink = false,
    isAnimated = false,
    href,
    className,
    wrapperClassName,
    ...props
  }: PropsWithChildren<Props>,
  ref: ForwardedRef<HTMLAnchorElement>
) => {
  const commonAnimatedTextStyle =
    'absolute flex flex-row gap-2 h-full w-full items-center justify-center transition-all duration-300';

  const renderContent = () => {
    if (isAnimated && suffixIcon) {
      return (
        <>
          <div className="flex flex-row items-center justify-center gap-2 opacity-0">
            {prefixIcon && (
              <Icon
                icon={prefixIcon}
                className={classNames('text-xl', prefixIconClassName)}
              />
            )}
            {children}
            <Icon
              icon={suffixIcon}
              className={classNames('text-xl', suffixIconClassName)}
            />
          </div>
          <div
            className={classNames(
              'top-0 group-hover/navButton:-top-full',
              commonAnimatedTextStyle
            )}
          >
            {prefixIcon && (
              <Icon
                icon={prefixIcon}
                className={classNames('text-xl', prefixIconClassName)}
              />
            )}
            {children}
            <Icon
              icon={suffixIcon}
              className={classNames(
                'text-xl rotate-[135deg]',
                suffixIconClassName
              )}
            />
          </div>
          <div
            className={classNames(
              'top-full group-hover/navButton:top-0',
              commonAnimatedTextStyle
            )}
          >
            {prefixIcon && (
              <Icon
                icon={prefixIcon}
                className={classNames('text-xl', prefixIconClassName)}
              />
            )}
            {children}
            <Icon
              icon={suffixIcon}
              className={classNames('text-xl rotate-180', suffixIconClassName)}
            />
          </div>
        </>
      );
    }

    return (
      <>
        {prefixIcon && (
          <Icon
            icon={prefixIcon}
            className={classNames('text-xl', prefixIconClassName)}
          />
        )}
        {children}
        {suffixIcon && (
          <Icon
            icon={suffixIcon}
            className={classNames('text-xl', suffixIconClassName)}
          />
        )}
      </>
    );
  };

  const buttonContent = (
    <div
      className={classNames(
        buttonStyles({ variant, size }),
        'text-body-small font-inter',
        isAnimated && 'group/navButton relative overflow-hidden',
        className
      )}
    >
      {renderContent()}
    </div>
  );

  if (isInternalLink) {
    return (
      <Link
        href={href as LinkProps['href']}
        className={classNames('w-fit', wrapperClassName)}
      >
        {buttonContent}
      </Link>
    );
  }

  return (
    <a
      ref={ref}
      href={href}
      className={classNames('w-fit', wrapperClassName)}
      {...props}
    >
      {buttonContent}
    </a>
  );
};

const NavigationButton = forwardRef(NavigationButtonComponent);
export default NavigationButton;
